import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ReportsSectionCompact.css";
import { useAuth } from "../context/AuthContext";
import {
  exportSuperAdminGiftCardAnalytics,
  getSuperAdminGiftCardAnalyticsFilters,
  postSuperAdminGiftCardAnalytics,
} from "../api/superadminReports";
import { getSalonTimezone } from "../utils/salonTimezone";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import useTableSort from "../hooks/useTableSort";
import { GIFT_CARD_ANALYTICS_TOOLTIPS } from "../constants/reportTooltips";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toFixed(digits);
};

const ALERT_CLASS =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700";

const FIELD_CLASS =
  "h-10 px-3 text-sm md:text-base rounded-xl border border-slate-300 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50";

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "gift-card-analytics",
  title: "Gift Card Analytics (Super Admin)",
  subtitle:
    "Track gift card sales, redemptions, balances, and liability across scope.",
  summaryLabel: "Gift Card Summary",
  detailsLabel: "Gift Card Details",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("gift-card-analytics");

const LABEL_TEXT = "text-xs font-semibold uppercase tracking-wide text-slate-500";

const BTN_PRIMARY =
  "h-10 px-5 rounded-full bg-rose-500 text-white text-sm md:text-base font-semibold hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_OUTLINE =
  "h-10 px-5 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400";

const BTN_EXPORT =
  "h-9 px-4 rounded-full border border-rose-200 text-rose-600 text-sm font-semibold hover:bg-rose-50";

const TAB_BASE =
  "h-9 md:h-10 px-4 rounded-full text-sm font-semibold transition";

const TAB_ACTIVE = "bg-rose-500 text-white";

const TAB_INACTIVE = "border border-slate-300 text-slate-700 hover:border-slate-400";

const TABS = [
  { key: "giftCardsSold", label: "Gift Cards Sold" },
  { key: "balancesAsOf", label: "Balances As Of" },
  { key: "clientsWithBalance", label: "Clients With Balance" },
  { key: "salesRedemptions", label: "Sales & Redemptions" },
  { key: "liabilitySummary", label: "Liability Summary" },
];

const TAB_TO_API = {
  giftCardsSold: "sold",
  balancesAsOf: "balances_as_of",
  clientsWithBalance: "clients_with_balance",
  salesRedemptions: "sales_redemptions",
  liabilitySummary: "liability_summary",
};

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const SUPERADMIN_SCOPE_TOOLTIPS = {
  scope:
    "Choose report reach: all salons, one business, or one salon. Date filters use each salon's local timezone.",
  business:
    "Optional for All Salons. Required for Business scope. If selected with Salon scope, salon must belong to this business.",
  salon:
    "Required for Salon scope. Optional in broader scope to narrow to one salon.",
  show_scope_columns:
    "When enabled, includes Business and Salon columns in report tables and CSV exports.",
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export default function SuperAdminGiftCardAnalytics() {
  const { currentSalon, is_super_admin, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((row) => {
      if (!row) return;
      map.set(String(row.id), row);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((row) => {
      if (!row) return;
      map.set(String(row.id), row);
    });
    return map;
  }, [salons]);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons || [];
    return (salons || []).filter(
      (row) => String(row.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (selectedSalon?.timezone) return selectedSalon.timezone;
    }
    if (businessId) {
      const selectedBusiness = businessMap.get(String(businessId));
      if (selectedBusiness?.timezone) return selectedBusiness.timezone;
    }
    return salonTimezone;
  }, [salonId, salonMap, businessId, businessMap, salonTimezone]);

  const today = formatDateISO(new Date(), anchorTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -6), anchorTimezone);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [asOfDate, setAsOfDate] = useState(today);
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [minBalance, setMinBalance] = useState("");
  const [maxBalance, setMaxBalance] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [includeAnonymous, setIncludeAnonymous] = useState(true);
  const [groupBy, setGroupBy] = useState("gift_card");
  const [activeTab, setActiveTab] = useState("giftCardsSold");
  const [showScopeColumns, setShowScopeColumns] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cache, setCache] = useState({});
  const lastEndDateRef = useRef(endDate);

  useEffect(() => {
    if (!endDate) return;
    if (!asOfDate || asOfDate === lastEndDateRef.current) {
      setAsOfDate(endDate);
    }
    lastEndDateRef.current = endDate;
  }, [endDate, asOfDate]);

  useEffect(() => {
    if (!is_super_admin || authLoading) return;
    let mounted = true;
    const loadFilters = async () => {
      setFiltersLoading(true);
      try {
        const data = await getSuperAdminGiftCardAnalyticsFilters({
          scope,
          business_id: businessId || undefined,
          salon_id: salonId || undefined,
        });
        if (!mounted) return;
        setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
        setSalons(Array.isArray(data?.salons) ? data.salons : []);
      } catch (err) {
        if (!mounted) return;
        setBusinesses([]);
        setSalons([]);
        setError(err?.message || "Unable to load scope filters.");
      } finally {
        if (mounted) setFiltersLoading(false);
      }
    };

    loadFilters();
    return () => {
      mounted = false;
    };
  }, [is_super_admin, authLoading, scope, businessId, salonId]);

  useEffect(() => {
    setShowScopeColumns(scope !== "salon");
  }, [scope]);

  const togglePillClass = (active) =>
    `${TAB_BASE} ${active ? TAB_ACTIVE : TAB_INACTIVE}`;

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={GIFT_CARD_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const renderInlineLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <span className={LABEL_TEXT}>{label}</span>
      {tooltipKey ? (
        <InfoTooltip text={GIFT_CARD_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const handleScopeChange = (value) => {
    setScope(value);
    if (value === "all") {
      setBusinessId("");
      setSalonId("");
      return;
    }
    if (value === "business") {
      setSalonId("");
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!value && scope !== "salon") {
      setSalonId("");
      return;
    }
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (
        selectedSalon &&
        value &&
        String(selectedSalon.business_id || "") !== String(value)
      ) {
        setSalonId("");
      }
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const resetFilters = () => {
    setScope("all");
    setBusinessId("");
    setSalonId("");
    setShowScopeColumns(true);
    setStartDate(defaultStart);
    setEndDate(today);
    setAsOfDate(today);
    setMinAmount("");
    setMaxAmount("");
    setMinBalance("");
    setMaxBalance("");
    setClientQuery("");
    setIncludeAnonymous(true);
    setGroupBy("gift_card");
  };

  const filters = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      as_of_date: asOfDate,
      scope,
      business_id: businessId ? Number(businessId) : undefined,
      salon_id: salonId ? Number(salonId) : undefined,
      min_amount: normalizeNumber(minAmount),
      max_amount: normalizeNumber(maxAmount),
      min_balance: normalizeNumber(minBalance),
      max_balance: normalizeNumber(maxBalance),
      client_search: clientQuery.trim() ? clientQuery.trim() : null,
      include_anonymous: includeAnonymous,
      group_by: groupBy,
      include_business_salon_cols: showScopeColumns,
    }),
    [
      scope,
      businessId,
      salonId,
      startDate,
      endDate,
      asOfDate,
      minAmount,
      maxAmount,
      minBalance,
      maxBalance,
      clientQuery,
      includeAnonymous,
      groupBy,
      showScopeColumns,
    ]
  );

  const cacheKey = useMemo(
    () => `${activeTab}:${JSON.stringify(filters)}`,
    [activeTab, filters]
  );

  const activeData = cache[cacheKey];

  const runReport = useCallback(async () => {
    if (scope === "business" && !businessId) {
      setError("Choose a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setError("Choose a salon for Salon scope.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    if (!asOfDate) {
      setError("Select an as-of date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await postSuperAdminGiftCardAnalytics({
        ...filters,
        active_tab: TAB_TO_API[activeTab],
      });
      let tabData = null;
      if (activeTab === "giftCardsSold") {
        tabData = data?.gift_cards_sold || null;
      } else if (activeTab === "balancesAsOf") {
        tabData = data?.balances_as_of || null;
      } else if (activeTab === "clientsWithBalance") {
        tabData = data?.clients_with_balance || null;
      } else if (activeTab === "salesRedemptions") {
        tabData = data?.sales_redemptions || null;
      } else if (activeTab === "liabilitySummary") {
        tabData = data?.liability_summary || null;
      }
      setCache((prev) => ({ ...prev, [cacheKey]: tabData }));
    } catch (err) {
      setError(
        err?.message ||
          err?.response?.data?.detail ||
          "Unable to run report."
      );
    } finally {
      setLoading(false);
    }
  }, [
    scope,
    businessId,
    salonId,
    startDate,
    endDate,
    asOfDate,
    filters,
    activeTab,
    cacheKey,
  ]);

  const exportActiveTab = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await exportSuperAdminGiftCardAnalytics({
        ...filters,
        active_tab: TAB_TO_API[activeTab],
      });
    } catch (err) {
      setError(err?.message || "Unable to export CSV.");
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab]);

  const selectedSalonTimezone = useMemo(() => {
    if (scope !== "salon" || !salonId) return "";
    const selectedSalon = salonMap.get(String(salonId));
    return selectedSalon?.timezone || anchorTimezone || "America/New_York";
  }, [scope, salonId, salonMap, anchorTimezone]);

  const timezoneNote = useMemo(() => {
    if (scope === "salon") {
      return `Salon timezone: ${selectedSalonTimezone}`;
    }
    return "Dates use each salon's local timezone.";
  }, [scope, selectedSalonTimezone]);

  const emptyState = (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
      Run the report to load this tab.
    </div>
  );

  const issuedColumns = useMemo(
    () => ({
      business: { type: "text", getValue: (row) => row?.business_name || "" },
      salon: { type: "text", getValue: (row) => row?.salon_name || "" },
      issued_date: { type: "date", getValue: (row) => row?.issued_at_local },
      code: { type: "text", getValue: (row) => row?.code || "" },
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      amount: { type: "number", getValue: (row) => row?.issued_amount },
      sold_by: { type: "text", getValue: (row) => row?.sold_by_employee || "" },
    }),
    []
  );

  const issuedSort = useTableSort({
    rows: activeData?.rows || [],
    columns: issuedColumns,
    fallbackKey: "issued_date",
  });

  const balancesGiftCardColumns = useMemo(
    () => ({
      business: { type: "text", getValue: (row) => row?.business_name || "" },
      salon: { type: "text", getValue: (row) => row?.salon_name || "" },
      code: { type: "text", getValue: (row) => row?.code || "" },
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      balance: { type: "number", getValue: (row) => row?.balance_as_of },
      last_activity: {
        type: "date",
        getValue: (row) => row?.last_activity_local,
      },
    }),
    []
  );

  const balancesGiftCardSort = useTableSort({
    rows: activeData?.rows || [],
    columns: balancesGiftCardColumns,
    fallbackKey: "code",
  });

  const balancesClientColumns = useMemo(
    () => ({
      business: { type: "text", getValue: (row) => row?.business_name || "" },
      salon: { type: "text", getValue: (row) => row?.salon_name || "" },
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      gift_cards_count: {
        type: "number",
        getValue: (row) => row?.gift_cards_count,
      },
      balance: { type: "number", getValue: (row) => row?.total_balance_as_of },
    }),
    []
  );

  const balancesClientSort = useTableSort({
    rows: activeData?.rows || [],
    columns: balancesClientColumns,
    fallbackKey: "client",
  });

  const clientsWithBalanceColumns = useMemo(
    () => ({
      business: { type: "text", getValue: (row) => row?.business_name || "" },
      salon: { type: "text", getValue: (row) => row?.salon_name || "" },
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      balance: {
        type: "number",
        getValue: (row) => row?.total_balance_as_of_end_date,
      },
      gift_cards_count: {
        type: "number",
        getValue: (row) => row?.gift_cards_count,
      },
      last_activity: {
        type: "date",
        getValue: (row) => row?.last_activity_local,
      },
    }),
    []
  );

  const clientsWithBalanceSort = useTableSort({
    rows: activeData?.rows || [],
    columns: clientsWithBalanceColumns,
    fallbackKey: "client",
  });

  const topClientsColumns = useMemo(
    () => ({
      business: { type: "text", getValue: (row) => row?.business_name || "" },
      salon: { type: "text", getValue: (row) => row?.salon_name || "" },
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      balance: { type: "number", getValue: (row) => row?.total_balance },
    }),
    []
  );

  const topClientsSort = useTableSort({
    rows: activeData?.top_clients_by_balance || [],
    columns: topClientsColumns,
    fallbackKey: "client",
  });

  const balancesRows =
    groupBy === "client" ? balancesClientSort.sortedRows : balancesGiftCardSort.sortedRows;

  if (!is_super_admin && !authLoading) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader
            title="Gift Card Analytics"
            closeTo="/superadmin/reports"
            titleAddon={
              <InfoTooltip
                text={GIFT_CARD_ANALYTICS_TOOLTIPS.report_scope}
                label="Gift Card Analytics"
              />
            }
          />

          <div className="px-6 py-6 space-y-6">
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && <div className={ALERT_CLASS}>{error}</div>}
            {(loading || filtersLoading) && <div className={ALERT_CLASS}>Loading...</div>}

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <div className="flex items-center gap-1.5">
                    <label className={LABEL_TEXT}>Scope</label>
                    <InfoTooltip text={SUPERADMIN_SCOPE_TOOLTIPS.scope} label="Scope" />
                  </div>
                  <select
                    value={scope}
                    onChange={(e) => handleScopeChange(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <label className={LABEL_TEXT}>Business</label>
                    <InfoTooltip text={SUPERADMIN_SCOPE_TOOLTIPS.business} label="Business" />
                  </div>
                  <select
                    value={businessId}
                    onChange={(e) => handleBusinessChange(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading}
                  >
                    <option value="">All businesses</option>
                    {businesses.map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.name || `Business ${business.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <label className={LABEL_TEXT}>Salon</label>
                    <InfoTooltip text={SUPERADMIN_SCOPE_TOOLTIPS.salon} label="Salon" />
                  </div>
                  <select
                    value={salonId}
                    onChange={(e) => handleSalonChange(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={filtersLoading}
                  >
                    <option value="">{scope === "salon" ? "Select salon" : "All salons"}</option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name || `Salon ${salon.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={showScopeColumns}
                      onChange={(e) => setShowScopeColumns(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
                    />
                    <span>Show Business/Salon columns</span>
                    <InfoTooltip
                      text={SUPERADMIN_SCOPE_TOOLTIPS.show_scope_columns}
                      label="Show Business/Salon columns"
                    />
                  </label>
                </div>
              </div>

              <div className="text-sm text-slate-600">{timezoneNote}</div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("As Of Date", "as_of_date")}
                  <input
                    type="date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Client Search", "client_search")}
                  <input
                    type="text"
                    value={clientQuery}
                    onChange={(e) => setClientQuery(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="Name, phone, email"
                  />
                </div>
                <div>
                  {renderFieldLabel("Min Amount", "min_amount")}
                  <input
                    type="number"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
                <div>
                  {renderFieldLabel("Max Amount", "max_amount")}
                  <input
                    type="number"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
                <div>
                  {renderFieldLabel("Min Balance", "min_balance")}
                  <input
                    type="number"
                    value={minBalance}
                    onChange={(e) => setMinBalance(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
                <div>
                  {renderFieldLabel("Max Balance", "max_balance")}
                  <input
                    type="number"
                    value={maxBalance}
                    onChange={(e) => setMaxBalance(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeAnonymous}
                      onChange={(e) => setIncludeAnonymous(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
                    />
                    <span>Include anonymous</span>
                  </label>
                  <InfoTooltip
                    text={GIFT_CARD_ANALYTICS_TOOLTIPS.include_anonymous}
                    label="Include anonymous"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className={BTN_OUTLINE}
                    disabled={loading || filtersLoading}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={runReport}
                    className={BTN_PRIMARY}
                    disabled={loading || filtersLoading}
                  >
                    Run Report
                  </button>
                </div>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.export}
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                HELP_ANCHORS.export
              )}`}
            >
              <div className="text-base font-semibold text-slate-900">Export</div>
              <div className="mt-1 text-sm text-slate-600">
                Export is available in each tab after results are loaded.
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {TABS.map((tab) => {
                const tooltip = GIFT_CARD_ANALYTICS_TOOLTIPS[`tab_${tab.key}`];
                return (
                  <div key={tab.key} className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className={`${TAB_BASE} ${
                        activeTab === tab.key ? TAB_ACTIVE : TAB_INACTIVE
                      }`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                    {tooltip ? <InfoTooltip text={tooltip} label={tab.label} /> : null}
                  </div>
                );
              })}
            </div>

            <div
              id={HELP_ANCHORS.kpis}
              className={`space-y-6 ${getSectionClassName(HELP_ANCHORS.kpis)}`}
            >
              {activeTab === "giftCardsSold" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Count Sold
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatNumber(activeData?.totals?.count, 0)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Total Sold Amount
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.totals?.total_issued_amount)}
                          </div>
                        </div>
                      </div>

                      <div id={HELP_ANCHORS.details} className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(HELP_ANCHORS.details)}`}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                          <div className="text-sm font-semibold text-slate-700">
                            Issued Gift Cards
                          </div>
                          <button
                            type="button"
                            className={BTN_EXPORT}
                            onClick={exportActiveTab}
                          >
                            Export CSV
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                {showScopeColumns && (
                                  <>
                                    <SortableTh
                                      label="Business"
                                      sortKey="business"
                                      sortState={issuedSort.sortState}
                                      onSort={issuedSort.handleSort}
                                      className="px-4 py-3 report-th-left"
                                      align="left"
                                    />
                                    <SortableTh
                                      label="Salon"
                                      sortKey="salon"
                                      sortState={issuedSort.sortState}
                                      onSort={issuedSort.handleSort}
                                      className="px-4 py-3 report-th-left"
                                      align="left"
                                    />
                                  </>
                                )}
                                <SortableTh
                                  label="Issued Date"
                                  sortKey="issued_date"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_issued_date}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Gift Card Code/ID"
                                  sortKey="code"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_code}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Client"
                                  sortKey="client"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Issued Amount"
                                  sortKey="amount"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_amount}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Sold By"
                                  sortKey="sold_by"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_sold_by}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                              </tr>
                            </thead>
                            <tbody>
                              {issuedSort.sortedRows.map((row, index) => (
                                <tr
                                  key={`${row.code || "row"}-${index}`}
                                  className="border-t border-slate-100"
                                >
                                  {showScopeColumns && (
                                    <>
                                      <td className="px-4 py-3 report-td-left text-slate-700">
                                        {row.business_name || "--"}
                                      </td>
                                      <td className="px-4 py-3 report-td-left text-slate-700">
                                        {row.salon_name || "--"}
                                      </td>
                                    </>
                                  )}
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.issued_at_local || "--"}
                                  </td>
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.code || "--"}
                                  </td>
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.client_name || "Anonymous"}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.issued_amount)}
                                  </td>
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.sold_by_employee || "--"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "balancesAsOf" && (
                <div className="space-y-6">
                <div className="flex flex-wrap items-center gap-2">
                    {renderInlineLabel("Group By", "group_by")}
                    <button
                      type="button"
                      className={togglePillClass(groupBy === "gift_card")}
                      onClick={() => setGroupBy("gift_card")}
                    >
                      Gift Card
                    </button>
                    <button
                      type="button"
                      className={togglePillClass(groupBy === "client")}
                      onClick={() => setGroupBy("client")}
                    >
                      Client
                    </button>
                  </div>

                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Count
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatNumber(activeData?.totals?.count, 0)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Total Balance
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.totals?.total_balance)}
                          </div>
                        </div>
                      </div>

                      <div id={HELP_ANCHORS.details} className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(HELP_ANCHORS.details)}`}>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                          <div className="text-sm font-semibold text-slate-700">
                            Balances As Of
                          </div>
                          <button
                            type="button"
                            className={BTN_EXPORT}
                          onClick={exportActiveTab}
                          >
                            Export CSV
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              {groupBy === "client" ? (
                                <tr>
                                  {showScopeColumns && (
                                    <>
                                      <SortableTh
                                        label="Business"
                                        sortKey="business"
                                        sortState={balancesClientSort.sortState}
                                        onSort={balancesClientSort.handleSort}
                                        className="px-4 py-3 report-th-left"
                                        align="left"
                                      />
                                      <SortableTh
                                        label="Salon"
                                        sortKey="salon"
                                        sortState={balancesClientSort.sortState}
                                        onSort={balancesClientSort.handleSort}
                                        className="px-4 py-3 report-th-left"
                                        align="left"
                                      />
                                    </>
                                  )}
                                  <SortableTh
                                    label="Client"
                                    sortKey="client"
                                    sortState={balancesClientSort.sortState}
                                    onSort={balancesClientSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                  <SortableTh
                                    label="Gift Cards Count"
                                    sortKey="gift_cards_count"
                                    sortState={balancesClientSort.sortState}
                                    onSort={balancesClientSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_gift_cards_count}
                                    className="px-4 py-3 report-th-center"
                                    align="center"
                                  />
                                  <SortableTh
                                    label="Total Balance As Of"
                                    sortKey="balance"
                                    sortState={balancesClientSort.sortState}
                                    onSort={balancesClientSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_total_balance}
                                    className="px-4 py-3 report-th-center"
                                    align="center"
                                  />
                                </tr>
                              ) : (
                                <tr>
                                  {showScopeColumns && (
                                    <>
                                      <SortableTh
                                        label="Business"
                                        sortKey="business"
                                        sortState={balancesGiftCardSort.sortState}
                                        onSort={balancesGiftCardSort.handleSort}
                                        className="px-4 py-3 report-th-left"
                                        align="left"
                                      />
                                      <SortableTh
                                        label="Salon"
                                        sortKey="salon"
                                        sortState={balancesGiftCardSort.sortState}
                                        onSort={balancesGiftCardSort.handleSort}
                                        className="px-4 py-3 report-th-left"
                                        align="left"
                                      />
                                    </>
                                  )}
                                  <SortableTh
                                    label="Gift Card Code/ID"
                                    sortKey="code"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_code}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                  <SortableTh
                                    label="Client"
                                    sortKey="client"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                  <SortableTh
                                    label="Balance As Of"
                                    sortKey="balance"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_balance}
                                    className="px-4 py-3 report-th-center"
                                    align="center"
                                  />
                                  <SortableTh
                                    label="Last Activity"
                                    sortKey="last_activity"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_last_activity}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                </tr>
                              )}
                            </thead>
                            <tbody>
                              {balancesRows.map((row, index) =>
                                groupBy === "client" ? (
                                  <tr
                                    key={`${row.client_id || "row"}-${index}`}
                                    className="border-t border-slate-100"
                                  >
                                    {showScopeColumns && (
                                      <>
                                        <td className="px-4 py-3 report-td-left text-slate-700">
                                          {row.business_name || "--"}
                                        </td>
                                        <td className="px-4 py-3 report-td-left text-slate-700">
                                          {row.salon_name || "--"}
                                        </td>
                                      </>
                                    )}
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.client_name || "Anonymous"}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatNumber(row.gift_cards_count, 0)}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatMoney(row.total_balance_as_of)}
                                    </td>
                                  </tr>
                                ) : (
                                  <tr
                                    key={`${row.code || "row"}-${index}`}
                                    className="border-t border-slate-100"
                                  >
                                    {showScopeColumns && (
                                      <>
                                        <td className="px-4 py-3 report-td-left text-slate-700">
                                          {row.business_name || "--"}
                                        </td>
                                        <td className="px-4 py-3 report-td-left text-slate-700">
                                          {row.salon_name || "--"}
                                        </td>
                                      </>
                                    )}
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.code || "--"}
                                    </td>
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.client_name || "Anonymous"}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatMoney(row.balance_as_of)}
                                    </td>
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.last_activity_local || "--"}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "clientsWithBalance" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <div id={HELP_ANCHORS.details} className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(HELP_ANCHORS.details)}`}>
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                        <div className="text-sm font-semibold text-slate-700">
                          Clients With Balance
                        </div>
                        <button
                          type="button"
                          className={BTN_EXPORT}
                          onClick={exportActiveTab}
                        >
                          Export CSV
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm report-table">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              {showScopeColumns && (
                                <>
                                  <SortableTh
                                    label="Business"
                                    sortKey="business"
                                    sortState={clientsWithBalanceSort.sortState}
                                    onSort={clientsWithBalanceSort.handleSort}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                  <SortableTh
                                    label="Salon"
                                    sortKey="salon"
                                    sortState={clientsWithBalanceSort.sortState}
                                    onSort={clientsWithBalanceSort.handleSort}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                </>
                              )}
                              <SortableTh
                                label="Client"
                                sortKey="client"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                              <SortableTh
                                label="Total Balance As Of End Date"
                                sortKey="balance"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_total_balance}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Gift Cards Count"
                                sortKey="gift_cards_count"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_gift_cards_count}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Last Activity"
                                sortKey="last_activity"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_last_activity}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                            </tr>
                          </thead>
                          <tbody>
                            {clientsWithBalanceSort.sortedRows.map((row, index) => (
                              <tr
                                key={`${row.client_id || "row"}-${index}`}
                                className="border-t border-slate-100"
                              >
                                {showScopeColumns && (
                                  <>
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.business_name || "--"}
                                    </td>
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.salon_name || "--"}
                                    </td>
                                  </>
                                )}
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  {row.client_name || "Anonymous"}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {formatMoney(row.total_balance_as_of_end_date)}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {formatNumber(row.gift_cards_count, 0)}
                                </td>
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  {row.last_activity_local || "--"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "salesRedemptions" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Issued Total
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.issued_total)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Redeemed Total
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.redeemed_total)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Adjustments Total
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.adjustments_total)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Net Change
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.net_change)}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "liabilitySummary" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <div className="text-xs uppercase text-slate-500">
                          Liability As Of
                        </div>
                        <div className="text-3xl font-semibold text-slate-900">
                          {formatMoney(activeData?.liability_as_of)}
                        </div>
                      </div>

                      {Array.isArray(activeData?.top_clients_by_balance) &&
                        activeData.top_clients_by_balance.length > 0 && (
                          <div id={HELP_ANCHORS.details} className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(HELP_ANCHORS.details)}`}>
                            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                              <div className="text-sm font-semibold text-slate-700">
                                Top Clients by Balance
                              </div>
                              <button
                                type="button"
                                className={BTN_EXPORT}
                                onClick={exportActiveTab}
                              >
                                Export CSV
                              </button>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm report-table">
                                <thead className="bg-slate-50 text-slate-600">
                                  <tr>
                                    {showScopeColumns && (
                                      <>
                                        <SortableTh
                                          label="Business"
                                          sortKey="business"
                                          sortState={topClientsSort.sortState}
                                          onSort={topClientsSort.handleSort}
                                          className="px-4 py-3 report-th-left"
                                          align="left"
                                        />
                                        <SortableTh
                                          label="Salon"
                                          sortKey="salon"
                                          sortState={topClientsSort.sortState}
                                          onSort={topClientsSort.handleSort}
                                          className="px-4 py-3 report-th-left"
                                          align="left"
                                        />
                                      </>
                                    )}
                                    <SortableTh
                                      label="Client"
                                      sortKey="client"
                                      sortState={topClientsSort.sortState}
                                      onSort={topClientsSort.handleSort}
                                      tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                      className="px-4 py-3 report-th-left"
                                      align="left"
                                    />
                                    <SortableTh
                                      label="Balance"
                                      sortKey="balance"
                                      sortState={topClientsSort.sortState}
                                      onSort={topClientsSort.handleSort}
                                      tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_balance}
                                      className="px-4 py-3 report-th-center"
                                      align="center"
                                    />
                                  </tr>
                                </thead>
                                <tbody>
                                  {topClientsSort.sortedRows.map(
                                    (row, index) => (
                                      <tr
                                        key={`${row.client_id || "row"}-${index}`}
                                        className="border-t border-slate-100"
                                      >
                                        {showScopeColumns && (
                                          <>
                                            <td className="px-4 py-3 report-td-left text-slate-700">
                                              {row.business_name || "--"}
                                            </td>
                                            <td className="px-4 py-3 report-td-left text-slate-700">
                                              {row.salon_name || "--"}
                                            </td>
                                          </>
                                        )}
                                        <td className="px-4 py-3 report-td-left text-slate-700">
                                          {row.client_name || "Client"}
                                        </td>
                                        <td className="px-4 py-3 report-td-center text-slate-700">
                                          {formatMoney(row.total_balance)}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

